import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { ENDPOINTS } from '../../utilities/endpoints'

/**
 * 1) Checks whether the user is already onboarded by hitting
 *    GET /stripe/webhooks/return without following redirects.
 *    If Stripe isn’t done it will 302 → …?status=0…,
 *    otherwise 302 → …?status=1… or a 200.
 */
export const checkOnboarding = createAsyncThunk<
  { isStripeIntegrated: boolean },
  void,
  { rejectValue: string }
>(
  'onboarding/checkStatus',
  async (_, { rejectWithValue }) => {
    try {
      const jwt    = localStorage.getItem('token')
      const userId = localStorage.getItem('userId')
      if (!jwt || !userId) throw new Error('Missing auth or userId')

      const url = ENDPOINTS.STRIPE.RETURN(userId, false)
      const res = await fetch(url, {
        method:  'GET',
        headers: { 'Accept': 'application/json', 'Authorization': jwt },
        redirect: 'manual'
      })

      if (res.status === 302) {
        const location = res.headers.get('Location')!
        const params = new URL(location, window.location.origin).searchParams
        const isOK = params.get('status') === '1'
        return { isStripeIntegrated: isOK }
      }
      if (res.status === 200) return { isStripeIntegrated: true }
      throw new Error(`Unexpected status code: ${res.status}`)
    } catch (err: any) {
      return rejectWithValue(err.message ?? 'Failed to check onboarding')
    }
  }
)

/**
 * 2) Generates a new account link to start or continue onboarding.
 *    Calls your existing /stripe/webhooks/reauth endpoint (REAUTH).
 */
export const generateOnboardingLink = createAsyncThunk<
  { url: string },
  void,
  { rejectValue: string }
>(
  'onboarding/generateLink',
  async (_, { rejectWithValue }) => {
    try {
      const jwt    = localStorage.getItem('token')
      const userId = localStorage.getItem('userId')
      if (!jwt || !userId) throw new Error('Missing auth or userId')

      const url = ENDPOINTS.STRIPE.REAUTH(userId)
      const res = await fetch(url, {
        method:  'GET',
        headers: { 'Accept': 'application/json', 'Authorization': jwt },
        redirect: 'manual'
      })

      if (res.status === 302) {
        const location = res.headers.get('Location')
        if (!location) throw new Error('No Location header on redirect')
        return { url: location }
      }
      throw new Error(`Unexpected status code: ${res.status}`)
    } catch (err: any) {
      return rejectWithValue(err.message ?? 'Failed to start onboarding')
    }
  }
)

/**
 * 3) Calls the new backend GET /stripe/onboarding endpoint,
 *    returning a redirectLink for Stripe onboarding.
 */
export const startOnboarding = createAsyncThunk<
  { redirectLink: string },
  void,
  { rejectValue: string }
>(
  'onboarding/startOnboarding',
  async (_, { rejectWithValue }) => {
    try {
      const jwt = localStorage.getItem('token')
      if (!jwt) throw new Error('Missing auth token')

      const res = await fetch(ENDPOINTS.USERS.ONBOARDING, {
        method:  'GET',
        headers: { 'Accept': 'application/json', 'Authorization': jwt }
      })
      const body = await res.json()
      if (!res.ok) {
        const msg = body?.data?.message || body.message || res.statusText
        throw new Error(msg)
      }
      return { redirectLink: body.data.redirectLink }
    } catch (err: any) {
      return rejectWithValue(err.message ?? 'Failed to get onboarding link')
    }
  }
)

interface OnboardingState {
  isStripeIntegrated: boolean
  loading:            boolean
  error:              string | null
  redirectLink:       string | null
}

const initialState: OnboardingState = {
  isStripeIntegrated: false,
  loading:            false,
  error:              null,
  redirectLink:       null
}

const onboardingSlice = createSlice({
  name: 'onboarding',
  initialState,
  reducers: {
    resetOnboardingState(state) {
      state.isStripeIntegrated = false
      state.loading            = false
      state.error              = null
      state.redirectLink       = null
    }
  },
  extraReducers: (builder) => {
    builder
      // checkOnboarding
      .addCase(checkOnboarding.pending, (state) => {
        state.loading = true
        state.error   = null
      })
      .addCase(checkOnboarding.fulfilled, (state, action) => {
        state.loading            = false
        state.isStripeIntegrated = action.payload.isStripeIntegrated
      })
      .addCase(checkOnboarding.rejected, (state, action) => {
        state.loading = false
        state.error   = action.payload ?? action.error.message ?? 'Unknown error'
      })
      // generateOnboardingLink
      .addCase(generateOnboardingLink.pending, (state) => {
        state.loading = true
        state.error   = null
      })
      .addCase(generateOnboardingLink.fulfilled, (state) => {
        state.loading = false
      })
      .addCase(generateOnboardingLink.rejected, (state, action) => {
        state.loading = false
        state.error   = action.payload ?? action.error.message ?? 'Unknown error'
      })
      // startOnboarding
      .addCase(startOnboarding.pending, (state) => {
        state.loading      = true
        state.error        = null
        state.redirectLink = null
      })
      .addCase(startOnboarding.fulfilled, (state, action) => {
        state.loading      = false
        state.redirectLink = action.payload.redirectLink
      })
      .addCase(startOnboarding.rejected, (state, action) => {
        state.loading      = false
        state.error        = action.payload ?? action.error.message ?? 'Unknown error'
      })
  }
})

export const { resetOnboardingState } = onboardingSlice.actions
export default onboardingSlice.reducer